import * as React from 'react';
import cx from 'classnames';
import { isFunction } from 'lodash';

import { Tooltip } from 'antd';
import { EditorState } from 'draft-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faImage, faPlus, faQuoteLeft, faMinus, faPaperclip, faTable, faStrikethrough, faVideo } from '@fortawesome/free-solid-svg-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { TMediaType } from './types';

const { useState } = React;

const MEDIA_TYPE_ICON: {
  [key in TMediaType]: React.ReactNode;
} = {
  link: <FontAwesomeIcon icon={faLink} className="h-3 w-3 text-primary" />,
  attachment: <FontAwesomeIcon icon={faPaperclip} className="h-3 w-3 text-primary" />,
  'inline-image': <FontAwesomeIcon icon={faImage} className="h-3 w-3 text-primary" />,
  'inline-video': <FontAwesomeIcon icon={faVideo} className="h-3 w-3 text-primary" />,
  'cta-button': <FontAwesomeIcon icon={faPlus} className="h-3 w-3 text-primary" />,
  quote: <FontAwesomeIcon icon={faQuoteLeft} className="h-3 w-3 text-primary" />,
  divider: <FontAwesomeIcon icon={faMinus} className="h-3 w-3 text-primary" />,
  table: <FontAwesomeIcon icon={faTable} className="h-3 w-3 text-primary" />,
  strikethrough: <FontAwesomeIcon icon={faStrikethrough} className="h-3 w-3 text-primary" />,
};

const MEDIA_TYPE_LABEL: {
  [key in TMediaType]: string;
} = {
  link: 'Link',
  attachment: 'Attach file',
  'inline-image': 'Image',
  'inline-video': 'Video',
  'cta-button': 'Button',
  quote: 'Quote',
  divider: 'Divider',
  table: 'Table',
  strikethrough: 'Strikethrough',
};

const MEDIA_TYPE_HOTKEY: {
  [key in TMediaType]?: string;
} = {
  link: '⌘K',
  attachment: '⌘F',
  'inline-image': '⌘I',
  'inline-video': '⌘V',
  'cta-button': '⌘B',
  table: '⌘T',
  strikethrough: '⌘⇧X',
};

interface IProps {
  editorState: EditorState;
  getSelectedEntityType(): string;
  toggleMediaType(type: TMediaType, ref?: React.RefObject<HTMLInputElement>): void;
  onFilesSelected(files: FileList): void;
  toggleInlineStyle?: (style: 'STRIKETHROUGH' | 'CODE') => void;

  className?: string;
  emailV2?: boolean;
}

/**
 * @type {React.FC}
 */
export const MediaTypes: React.FC<IProps> = React.memo((props) => {
  const inputRef = React.useRef();
  const { getSelectedEntityType, emailV2 } = props;
  const selectedEntityType = getSelectedEntityType();

  const handleSelectedFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    props.onFilesSelected(files);

    event.target.value = '';
  };

  // Dropdown types (inside dropdown)
  const filteredTypes = React.useMemo(() => {
    const types: TMediaType[] = ['link'];

    if (isFunction(props.onFilesSelected)) {
      // Only add other media types when emailV2 is true
      if (emailV2) {
        types.push('inline-video');
        types.push('cta-button');
        types.push('quote');
        types.push('divider');
        types.push('strikethrough');
        types.push('table');
      }
    }

    return types;
  }, [props.onFilesSelected, emailV2]);

  const [dropdownVisible, setDropdownVisible] = useState(false);

  return (
    <div className="flex items-center gap-1">
      {/* Individual Attachment button */}
      <Tooltip title="Attach file">
        <div
          className="flex items-center justify-center w-6 h-6 rounded cursor-pointer transition-all duration-150 hover:bg-grey-2"
          onMouseDown={(event) => {
            event.preventDefault();
            props.toggleMediaType('attachment', inputRef);
          }}
        >
          <FontAwesomeIcon icon={faPaperclip} className="h-3 w-3 text-primary" />
        </div>
      </Tooltip>

      {/* Individual Image button */}
      {emailV2 && (
        <Tooltip title="Image">
          <div
            className="flex items-center justify-center w-6 h-6 rounded cursor-pointer transition-all duration-150 hover:bg-grey-2"
            onMouseDown={(event) => {
              event.preventDefault();
              props.toggleMediaType('inline-image', inputRef);
            }}
          >
            <FontAwesomeIcon icon={faImage} className="h-3 w-3 text-primary" />
          </div>
        </Tooltip>
      )}

      {/* Dropdown for remaining types */}
      <Popover open={dropdownVisible} onOpenChange={setDropdownVisible} modal>
        <PopoverTrigger asChild>
          <div
            className={cx(
                'flex items-center justify-center w-6 h-6 rounded cursor-pointer hover:bg-grey-2 transition-colors duration-150',
                {
                  'bg-grey-1': dropdownVisible,
                },
                props.className
              )}
            onMouseDown={(event) => event.preventDefault()}
          >
            <Tooltip title="Insert Element">
              <FontAwesomeIcon icon={faPlus} className="h-3 w-3 text-primary" />
            </Tooltip>
          </div>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-2 z-[9999] rounded-2xl">
          <div className="flex flex-col gap-1">
            {filteredTypes.map((type) => (
              <div
                key={type}
                className={cx(
                  'px-2 py-2 cursor-pointer hover:bg-grey-1 flex items-center gap-3 transition-colors duration-150 rounded',
                  {
                    'bg-blue-1 text-blue-5': selectedEntityType === type,
                  }
                )}
                onMouseDown={(event) => {
                  event.preventDefault();

                  // Handle inline styles differently
                  if (type === 'strikethrough') {
                    if (props.toggleInlineStyle) {
                      props.toggleInlineStyle(type.toUpperCase() as 'STRIKETHROUGH' | 'CODE');
                    }
                  } else {
                    props.toggleMediaType(type, inputRef);
                  }

                  setDropdownVisible(false);
                }}
              >
                <div className="flex items-center justify-center w-5 h-5">
                  {MEDIA_TYPE_ICON[type]}
                </div>
                <span className="font-medium flex-1 text-grey-6">{MEDIA_TYPE_LABEL[type]}</span>
                {MEDIA_TYPE_HOTKEY[type] && (
                  <span className="text-xs text-grey-4 bg-grey-1 px-1.5 py-0.5 rounded">
                    {MEDIA_TYPE_HOTKEY[type]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <input
        className="hidden"
        type="file"
        ref={inputRef}
        onChange={handleSelectedFilesChange}
      />
    </div>
  );
});

MediaTypes.displayName = 'MediaTypes';
